/**
 * Carrying an in-progress MCP connection across sign-in and onboarding.
 *
 * Someone connecting Claude who has no ApproveIt account has to sign up,
 * onboard, and then land back on the consent screen with the SAME
 * authorization parameters — otherwise the connection they started is lost
 * halfway through and the host just times out with nothing said.
 *
 * `sessionStorage`, not router state. The paths in between do full page
 * navigations (`window.location.href` on the region redirect, a fresh load
 * after onboarding), and React Router state does not survive those. It is
 * per-tab and cleared when the tab closes, which is the right lifetime for
 * "a connection this person started a minute ago".
 *
 * NOT a secret store. Everything kept here — client_id, redirect_uri,
 * code_challenge, scope, state — travels in the URL of the authorization
 * request by design; the code_challenge is public precisely so it can. No
 * token, code or verifier is ever written here, and the code the server
 * eventually issues is exchanged by the CLIENT, never by this page.
 *
 * One known gap: a cross-REGION login navigates to a different origin
 * (WEBAPP_EU_URL), and sessionStorage does not cross origins. That user
 * lands signed in on the right region with the connection dropped, which
 * is the behaviour they get today anyway.
 */

const KEY = 'approveit.mcp.pendingConnection'

// Long enough to sign up, verify an email and walk through onboarding;
// short enough that a stale entry cannot resurrect a connection attempt
// someone abandoned days ago in a tab they left open.
const MAX_AGE_MS = 60 * 60 * 1000

export interface PendingConnection {
  clientId: string
  redirectUri: string
  codeChallenge: string
  scope: string
  state?: string
  savedAt: number
}

/** The parameters that identify an authorization request, from the URL. */
export const readParams = (params: URLSearchParams): Omit<PendingConnection, 'savedAt'> | null => {
  const clientId = params.get('client_id')
  const redirectUri = params.get('redirect_uri')
  const codeChallenge = params.get('code_challenge')
  // The same three the consent screen requires. Storing a partial set
  // would resume into a screen that immediately reports a broken request.
  if (!clientId || !redirectUri || !codeChallenge) return null
  return {
    clientId,
    redirectUri,
    codeChallenge,
    scope: params.get('scope') || 'read',
    state: params.get('state') || undefined,
  }
}

export const savePending = (params: URLSearchParams): boolean => {
  const parsed = readParams(params)
  if (!parsed) return false
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...parsed, savedAt: Date.now() }))
    return true
  } catch {
    // Private-browsing modes and storage quotas both throw here. Losing the
    // resume is a worse experience, not a broken one — the user is still
    // signed in and can reconnect from the host.
    return false
  }
}

export const loadPending = (): PendingConnection | null => {
  let raw: string | null = null
  try {
    raw = sessionStorage.getItem(KEY)
  } catch {
    return null
  }
  if (!raw) return null
  try {
    // `unknown`, then narrowed. Casting to Partial<PendingConnection> tells
    // the compiler the fields are the right types when the whole point of
    // the checks below is that we do not know — so a mistake in them would
    // type-check cleanly and only show up as a broken resume.
    const parsed: unknown = JSON.parse(raw)
    // Explicit rather than load-bearing, and that is deliberate: without
    // it a primitive still fails the field checks below and `null` still
    // throws into the catch, so the OUTCOME is identical either way. What
    // it buys is refusing on purpose instead of by accident — the tests
    // pin the behaviour, not this line.
    if (typeof parsed !== 'object' || parsed === null) {
      clearPending()
      return null
    }
    const candidate = parsed as Record<string, unknown>
    // Re-validated on the way OUT, not trusted because we wrote it: another
    // tab, an extension or a stale build could have left something else
    // under this key, and resuming on a half-shaped object would send the
    // user to a consent screen that cannot work.
    if (
      typeof candidate.clientId !== 'string' ||
      typeof candidate.redirectUri !== 'string' ||
      typeof candidate.codeChallenge !== 'string' ||
      typeof candidate.savedAt !== 'number'
    ) {
      clearPending()
      return null
    }
    if (Date.now() - candidate.savedAt > MAX_AGE_MS) {
      clearPending()
      return null
    }
    return {
      clientId: candidate.clientId,
      redirectUri: candidate.redirectUri,
      codeChallenge: candidate.codeChallenge,
      scope: typeof candidate.scope === 'string' ? candidate.scope : 'read',
      state: typeof candidate.state === 'string' ? candidate.state : undefined,
      savedAt: candidate.savedAt,
    }
  } catch {
    clearPending()
    return null
  }
}

export const clearPending = (): void => {
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    // Nothing to do; a stale entry expires on its own.
  }
}

/** The consent URL to resume into — query string only, never an origin. */
export const pendingConsentSearch = (pending: PendingConnection): string => {
  const search = new URLSearchParams({
    client_id: pending.clientId,
    redirect_uri: pending.redirectUri,
    code_challenge: pending.codeChallenge,
    scope: pending.scope,
  })
  if (pending.state) search.set('state', pending.state)
  return `?${search.toString()}`
}
